import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DiagramCard from './DiagramCard';

const DiagramGroupManager = ({ diagramGroups, diagrams }) => {
  const [groups, setGroups] = useState([]);
  const [ungroupedDiagrams, setungroupedDiagrams] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    console.log('DiagramGroups prop:', diagramGroups);
    console.log('Diagrams prop:', diagrams);
    
    // Handle case where diagramGroups might be undefined or empty
    if (!diagramGroups || !Array.isArray(diagramGroups)) {
      console.log('No diagram groups provided, showing all diagrams as ungrouped');
      const allungrouped = diagrams.map((diagram) => ({ id: diagram.id, name: diagram.name })).sort((a, b) => a.name.localeCompare(b.name));
      setungroupedDiagrams(allungrouped);
      setGroups([]);
      return;
    }
    
    // Process existing diagram groups and populate with diagram objects
    const processedGroups = diagramGroups.map(group => {
      console.log('Processing group:', group);
      
      const groupDiagrams = (group.diagrams || []).map(diagramId => {
        console.log(`Looking for diagram ID: "${diagramId}" (type: ${typeof diagramId})`);
        console.log('Available diagram IDs:', diagrams.map(d => `"${d.id}" (type: ${typeof d.id})`));
        
        const diagram = diagrams.find(d => {
          const match = d.id == diagramId; // Using loose equality in case of type mismatch
          console.log(`Comparing ${d.id} == ${diagramId}: ${match}`);
          return match;
        });
        
        console.log(`Found diagram:`, diagram);
        return diagram ? { id: diagram.id, name: diagram.name } : null;
      }).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));
      
      return {
        id: group.id,
        name: group.name,
        diagrams: groupDiagrams
      };
    });

    console.log('Processed groups:', processedGroups);
    setGroups(processedGroups);

    // Find diagrams that are not in any group
    const allGroupedDiagramIds = diagramGroups.flatMap(group => group.diagrams || []);
    console.log('All grouped diagram IDs:', allGroupedDiagramIds);
    
    const ungrouped = diagrams.filter(
      (diagram) => !allGroupedDiagramIds.includes(diagram.id)
    );
    
    console.log('ungrouped diagrams:', ungrouped);
    
    // Sort ungrouped diagrams alphabetically by name
    const processedungrouped = ungrouped.map((diagram) => ({ id: diagram.id, name: diagram.name })).sort((a, b) => a.name.localeCompare(b.name));
    console.log('Processed ungrouped diagrams:', processedungrouped);
    setungroupedDiagrams(processedungrouped);
  }, [diagramGroups, diagrams]);

  const DroppableGroupContainer = ({ group }) => {
    const { isOver, setNodeRef } = useDroppable({
      id: `group-container-${group.id}`,
    });

    const style = {
      backgroundColor: isOver ? '#e0f2fe' : '#f9fafb',
      borderColor: isOver ? '#0284c7' : '#d1d5db',
      borderWidth: isOver ? '2px' : '1px',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`cards-container p-4 border rounded-md min-h-[75px] bg-gray-50 transition-all duration-200 ${
          isOver ? 'shadow-lg' : ''
        }`}
      >
        <SortableContext
          items={group.diagrams.map((d) => d.id)}
          strategy={verticalListSortingStrategy}
        >
          {group.diagrams.map((diagram) => (
            <DiagramCard 
              key={diagram.id} 
              diagram={diagram} 
              onUnlink={handleUnlinkDiagram}
              showUnlinkButton={true}
            />
          ))}
        </SortableContext>
        {group.diagrams.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            Drag diagrams here to group them
          </div>
        )}
        {isOver && group.diagrams.length > 0 && (
          <div className="text-center py-2 text-blue-600 text-sm font-medium">
            Drop here to add to group
          </div>
        )}
      </div>
    );
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    // Check if dragging from ungrouped diagrams
    const draggedDiagram = ungroupedDiagrams.find((d) => d.id === activeId);
    if (draggedDiagram) {
      // Check if dropping on a group container
      if (overId.toString().startsWith('group-container-')) {
        const targetGroupId = overId.replace('group-container-', '');
        setungroupedDiagrams((prev) => prev.filter((d) => d.id !== activeId));
        setGroups((prevGroups) =>
          prevGroups.map((group) =>
            group.id === targetGroupId
              ? { ...group, diagrams: [...group.diagrams, draggedDiagram].sort((a, b) => a.name.localeCompare(b.name)) }
              : group
          )
        );
        return;
      }
      
      // Check if dropping on another diagram (group them together)
      const targetGroup = groups.find((group) => group.diagrams.some((d) => d.id === overId));
      if (targetGroup) {
        setungroupedDiagrams((prev) => prev.filter((d) => d.id !== activeId));
        setGroups((prevGroups) =>
          prevGroups.map((group) =>
            group.id === targetGroup.id
              ? { ...group, diagrams: [...group.diagrams, draggedDiagram].sort((a, b) => a.name.localeCompare(b.name)) }
              : group
          )
        );
        return;
      }
    }

    // Check if dragging between groups or from a group to ungrouped diagrams
    const sourceGroup = groups.find((group) => group.diagrams.some((d) => d.id === activeId));
    
    if (sourceGroup) {
      const movedDiagram = sourceGroup.diagrams.find((d) => d.id === activeId);
      
      // Check if dropping on a group container
      if (overId.toString().startsWith('group-container-')) {
        const targetGroupId = overId.replace('group-container-', '');
        const targetGroup = groups.find((group) => group.id === targetGroupId);
        
        if (targetGroup && sourceGroup.id !== targetGroup.id) {
          setGroups((prevGroups) =>
            prevGroups.map((group) => {
              if (group.id === sourceGroup.id) {
                return {
                  ...group,
                  diagrams: group.diagrams.filter((d) => d.id !== activeId),
                };
              }
              if (group.id === targetGroup.id) {
                return {
                  ...group,
                  diagrams: [...group.diagrams, movedDiagram].sort((a, b) => a.name.localeCompare(b.name)),
                };
              }
              return group;
            })
          );
        }
        return;
      }
      
      // Check if dropping on another diagram in a different group
      const targetGroup = groups.find((group) => group.diagrams.some((d) => d.id === overId));
      if (targetGroup && sourceGroup.id !== targetGroup.id) {
        setGroups((prevGroups) =>
          prevGroups.map((group) => {
            if (group.id === sourceGroup.id) {
              return {
                ...group,
                diagrams: group.diagrams.filter((d) => d.id !== activeId),
              };
            }
            if (group.id === targetGroup.id) {
              return {
                ...group,
                diagrams: [...group.diagrams, movedDiagram].sort((a, b) => a.name.localeCompare(b.name)),
              };
            }
            return group;
          })
        );
        return;
      }
    }
    
    setActiveId(null);
  };

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;

    setGroups((prevGroups) => [
      ...prevGroups,
      {
        id: `group-${Date.now()}`,
        name: newGroupName,
        diagrams: [],
      },
    ]);
    setNewGroupName('');
  };

  const handleUnlinkDiagram = (diagramId) => {
    // Find the diagram in groups and move it to ungrouped diagrams
    const sourceGroup = groups.find((group) => group.diagrams.some((d) => d.id === diagramId));
    
    if (sourceGroup) {
      const diagramToUnlink = sourceGroup.diagrams.find((d) => d.id === diagramId);
      
      // Remove from group
      setGroups((prevGroups) =>
        prevGroups.map((group) =>
          group.id === sourceGroup.id
            ? { ...group, diagrams: group.diagrams.filter((d) => d.id !== diagramId) }
            : group
        )
      );
      
      // Add to ungrouped diagrams (sorted alphabetically)
      setungroupedDiagrams((prev) => 
        [...prev, diagramToUnlink].sort((a, b) => a.name.localeCompare(b.name))
      );
    }
  };

  const handleSaveGroups = async () => {
    try {
      // Prepare the data to send to the server
      const groupsData = groups.map(group => ({
        name: group.name,
        diagrams: group.diagrams.map(diagram => diagram.id)
      }));

      console.log('Saving groups:', groupsData);

      const response = await fetch('/diagram_groups/update_all', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ groups: groupsData })
      });

      const result = await response.json();

      if (result.success) {
        alert('Diagram Groups saved successfully!');
      } else {
        alert('Error saving groups: ' + result.error);
      }
    } catch (error) {
      console.error('Error saving groups:', error);
      alert('Error saving groups: ' + error.message);
    }
  };

  return (
    <div className="diagram-group-manager">

      <button className="pt-8 pb-8 hover:underline" onClick={handleSaveGroups}>Save Diagram Groups</button>

      <div className="new-group">
        <input
          type="text"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          placeholder="Enter new group name"
        />
        <br></br>
        <button className="pt-4 pb-8 hover:underline" onClick={handleCreateGroup}>Create Group</button>
      </div>

      <DndContext 
        sensors={sensors} 
        collisionDetection={closestCenter} 
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="groups-container">
          {groups.map((group) => (
            <div key={group.id} className="group-box">
              <h3>{group.name}</h3>
              <DroppableGroupContainer group={group} />
            </div>
          ))}
        </div>

        <div className="pt-4 ungrouped-diagrams">
          <h3>ungrouped Diagrams</h3>
          <SortableContext
            items={ungroupedDiagrams.map((d) => d.id)}
            strategy={verticalListSortingStrategy}
          >
            {ungroupedDiagrams.map((diagram) => (
              <DiagramCard key={diagram.id} diagram={diagram} />
            ))}
          </SortableContext>
        </div>

        <DragOverlay>
          {activeId ? <div className="drag-overlay">Dragging...</div> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default DiagramGroupManager;
